import React, {MouseEvent, useCallback, useContext, useMemo, useState, useRef} from 'react';
import {Button, CheckBox, Flex, Text} from '@priority-software/priority-style-react';
import {ChatBoxButton} from '../../../../../../components/Common/Chatbox/ChatBox';
import {texts} from '../../../../texts';
import {ruleDialogID, validForOptions} from '../../../../constants';
import {IAlert} from '../../../../actions/proto';
import {AutomationContext} from '../../../../context';
import {priority} from '@priority-software/priority-proto-api/lib';
import {
    AlertFormFields,
    AlertPermission,
    AlertValidForCategory,
    ChatboxSpecialCategory,
    RuleType
} from '../../../../enums';
import {isAlertSuperUser} from '../../../../utils';
import {findDefaultValidForCategory, isSaveEnabled} from './utils';
import {SelectControl} from '../AutomationRuleSelect/SelectControl';
import {AutomationChoose} from '../../../../reducer/model';
import {ModernTooltip} from '../../../../../../components/Common/Tooltip/ModernTooltip';
import {showFormClientSideMessage} from '../../../../../Form/actions/messages';
import styles from './style.module.scss';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {TEmailStateType} from '../../../../../EmailEditor/reducer/types';

type Props = {
    showValidForOptions: boolean;
    onClose: () => void;
    onRuleSave: () => Promise<void>;
    onRuleUpdate: (data: Partial<IAlert>) => void;
    handleRuleFormData: (data: Partial<priority.netitems.form.IAlertForm>) => void;
    type: RuleType;
    session: string;
    formName: string;
    newEmailTemplate?: TEmailStateType;
};

const resetValidForData = {
    [AlertFormFields.ValidMySelf]: undefined,
    [AlertFormFields.ValidAllUsers]: undefined,
    [AlertFormFields.ValidGroup]: [],
    [AlertFormFields.ValidUser]: [],
    [AlertFormFields.ValidUserInGroup]: undefined
};

export const AutomationRuleFooter = (props: Props) => {
    const {
        onClose,
        onRuleSave,
        handleRuleFormData,
        type,
        session,
        formName,
        showValidForOptions = true,
        newEmailTemplate
    } = props;
    const {openRule, choosesOptions} = useContext(AutomationContext);
    const isPending = useRef(false);
    const {ValidTypes} = choosesOptions;
    const [isChecked, setChecked] = useState(!!openRule?.Form?.MultiComp);
    const [categoryValue, setCategoryValue] = useState<number>(
        findDefaultValidForCategory(openRule?.Form)
    );
    const [showCategoryOptions, setShowCategoryOptions] = useState(Boolean(categoryValue));
    const isAdmin = isAlertSuperUser();
    const isValid = isSaveEnabled(openRule, type, newEmailTemplate);

    const [alertFormState, setAlertFormState] = useState<
        Partial<priority.netitems.form.IAlertForm>
    >(openRule.Form || {});

    const updateValidFor = useCallback(
        (validForData: Partial<priority.netitems.form.IAlertForm>) => {
            setAlertFormState(prevState => {
                return {...prevState, ...validForData};
            });
            handleRuleFormData(validForData);
        },
        [handleRuleFormData]
    );

    const handleValidFor = (e: MouseEvent<HTMLButtonElement>) => {
        const fieldName = e.currentTarget.dataset.value;
        const validForData = {...resetValidForData};
        if (
            fieldName === AlertFormFields.ValidMySelf ||
            fieldName === AlertFormFields.ValidAllUsers
        ) {
            validForData[fieldName] = true;
            setCategoryValue(0);
            setShowCategoryOptions(false);
        } else if (fieldName === ChatboxSpecialCategory.only) {
            setShowCategoryOptions(true);
            setCategoryValue(AlertValidForCategory.user);
        }
        updateValidFor(validForData);
    };

    const handleApplyToAllCompanies = useCallback(() => {
        const updatedValue = !isChecked;

        setChecked(updatedValue);
        handleRuleFormData({[AlertFormFields.MultiComp]: updatedValue});
    }, [handleRuleFormData, isChecked]);

    const handleValidForSelect = useCallback(
        (values: Array<number | string>, name: string) => {
            const validForData = {...resetValidForData};

            if (name) {
                validForData[name] = values as Array<number>;
                updateValidFor(validForData);
            }
        },
        [updateValidFor]
    );

    const handleCategoryChange = useCallback(
        (option: AutomationChoose) => {
            if (option.value === AlertValidForCategory.userInGroup) {
                updateValidFor({...resetValidForData, [AlertFormFields.ValidUserInGroup]: true});
            }
            setCategoryValue(option.value as number);
        },
        [updateValidFor]
    );

    const categoryFieldData = (() => {
        switch (categoryValue) {
            case AlertValidForCategory.user:
                return {
                    name: AlertFormFields.ValidUser,
                    options: choosesOptions.User,
                    placeholders: texts.select_user_placeholder,
                    defaultValue: alertFormState[AlertFormFields.ValidUser] as number[],
                    isMulti: true
                };
            case AlertValidForCategory.group:
                return {
                    name: AlertFormFields.ValidGroup,
                    options: choosesOptions.PrivilegeGroups,
                    placeholders: texts.select_group_placeholder,
                    defaultValue: alertFormState[AlertFormFields.ValidGroup] as number[],
                    isMulti: true
                };
            default:
                return null;
        }
    })();

    const appliedToMyselfFlag = alertFormState[AlertFormFields.ValidMySelf];

    const isEmptyUser = (alertFormState[AlertFormFields.ValidUser] || []).length === 0;
    const isEmptyGroup = (alertFormState[AlertFormFields.ValidGroup] || []).length === 0;

    const onSave = useCallback(async () => {
        if (isPending.current) return;
        try {
            isPending.current = true;
            if (isAdmin) {
                const showUserError = categoryValue === AlertValidForCategory.user && isEmptyUser;
                const showGroupError =
                    categoryValue === AlertValidForCategory.group && isEmptyGroup;

                if (showUserError || showGroupError) {
                    const title =
                        (type === RuleType.business
                            ? texts.business_rules_option_title
                            : texts.autofill_rules_option_title) +
                        ' - ' +
                        formName;

                    const content = showUserError
                        ? texts.save_rule_valid_for_user_error
                        : texts.save_rule_valid_for_group_error;

                    showFormClientSideMessage(
                        session,
                        priority.netitems.shared.Message.Type.Error,
                        {
                            title,
                            content,
                            confirmButtonText: texts.dlg_accept
                        }
                    );
                    return;
                }
            }
            await onRuleSave();
        } finally {
            isPending.current = false;
        }
    }, [onRuleSave, categoryValue, isAdmin, session, formName, type]);

    const saveBtn = useMemo(() => {
        const btn = (
            <Button
                disabled={!isValid}
                componentClasses={styles.btn}
                skin="primary"
                size="large"
                onClick={onSave}
            >
                {texts.rule_dialog_save}
            </Button>
        );

        if (!isValid) {
            return (
                <ModernTooltip
                    portalId={ruleDialogID}
                    tooltip={
                        type === RuleType.business
                            ? texts.save_business_rule_btn_validation_tooltip
                            : texts.save_autofill_rule_btn_validation_tooltip
                    }
                    position="top-end"
                    arrowOffset={10}
                    delay={100}
                >
                    {btn}
                </ModernTooltip>
            );
        }

        return btn;
    }, [onSave, isValid, type]);

    return (
        <div className={styles.component}>
            {showValidForOptions ? (
                <div className={styles.footerTop} {...createDataTestId('valid-for-section', false)}>
                    <Flex>
                        <Flex justifyContent="flex-start" componentClasses={styles.footerToolbar}>
                            <Text
                                textColor="primary-700"
                                tag="div"
                                skin="paragraph-1"
                                fontWeight="medium"
                                wrap={false}
                            >
                                {texts.valid_for_label}:
                            </Text>

                            {validForOptions.map(data => {
                                const isOnly =
                                    data.value === ChatboxSpecialCategory.only &&
                                    !appliedToMyselfFlag;
                                const isActive = isOnly
                                    ? showCategoryOptions
                                    : !!alertFormState[data.value];

                                const disabled =
                                    data.permission === AlertPermission.admin && !isAdmin;
                                return disabled ? null : (
                                    <ChatBoxButton
                                        key={data.value}
                                        size="medium"
                                        wrapClassName={styles.chatboxItem}
                                        value={data.value}
                                        text={texts[data.textField]}
                                        active={isActive}
                                        onClick={!isActive ? handleValidFor : undefined}
                                    />
                                );
                            })}

                            {isAdmin && !appliedToMyselfFlag && (
                                <>
                                    {showCategoryOptions && (
                                        <SelectControl
                                            componentClasses={styles.categoryDropdown}
                                            defaultValue={categoryValue}
                                            onChange={handleCategoryChange}
                                            options={ValidTypes}
                                            name="category"
                                        />
                                    )}
                                    {Boolean(categoryValue) && categoryFieldData && (
                                        <SelectControl
                                            componentClasses={styles.categoryDropdown}
                                            onMultiChange={handleValidForSelect}
                                            hideValue={false}
                                            {...categoryFieldData}
                                        />
                                    )}
                                </>
                            )}
                        </Flex>

                        <CheckBox
                            checked={isChecked}
                            onChange={handleApplyToAllCompanies}
                            {...createDataTestId('checkbox-block', true)}
                        >
                            <Text
                                skin="paragraph-2"
                                tag="span"
                                textColor="primary-700"
                                componentClasses={styles.nowrap}
                            >
                                {texts.apply_to_all_companies_label}
                            </Text>
                        </CheckBox>
                    </Flex>
                </div>
            ) : null}

            <div className={styles.footerBottom}>
                <Button
                    componentClasses={styles.btn}
                    skin="secondary"
                    size="large"
                    onClick={onClose}
                >
                    {texts.cancel_btn_label}
                </Button>

                {saveBtn}
            </div>
        </div>
    );
};
